// ============================================================================
// SmartSelect — searchable entity picker that ranks options as
//   1) existing matches inside the supplied scope (alphabetical, top 8)
//   2) "+ Add new {entity}"        (only when onCreateNew is provided)
//   3) "Show all" — expansion to the unfiltered set for this entity type
//
// Generalizes the pattern from `src/modules/copilot/cards/facility-combobox.tsx`
// (170 lines, vanilla React, mousedown selection to beat the click-outside
// listener). This component supports four entity types — service_provider,
// facility, vendor, supply — and is the first stop for any new dropdown that
// otherwise would show all 175+ facilities / 70+ providers / 19 vendors /
// 227 supplies flat.
//
// Implementation notes:
//   - Vanilla React + Tailwind. No new dependencies.
//   - Supabase JS client only (no PostgREST embeds — see the flat-fetch
//     pattern memo at docs/database-changes.md).
//   - `entity_aliases` schema uses (entity_type, entity_id) — NOT
//     (target_table, target_id) as the cowork prompt assumed. The alias
//     query is written against the real schema.
//   - `service_providers` has no direct `state` column. State scoping for
//     providers goes via service_assignments → facilities.state.
//   - At our scale (<300 of any entity) we filter client-side after a single
//     scoped fetch. Type-ahead, no debounce.
// ============================================================================

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { supabase } from '../shared/supabase';
import { subStatusLabel } from '../modules/subcontractors/stages';
import {
  fetchProjectRows,
  projectRowName,
  projectStageLabel,
  type ProjectRow,
} from '../modules/project-work/lib/project-rows';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type SmartSelectEntity =
  | 'service_provider'
  | 'facility'
  | 'vendor'
  | 'supply'
  | 'contact'
  /**
   * A punch-clock WORKER. 2026-09-22.
   *
   * ⛔ Ben, on trying it himself: "the picker for PUNCH tab of the time and
   * Labor module is broken. There is no 'night manager' filter, and I still
   * cannot find William." He was right about the symptom and the cause was not
   * that William is missing — he is in the pool, and so are FOUR other
   * Rodriguezes. It was a flat 116-name <select> with no typing and no order
   * anybody could reason about. TCSD-0171: SmartSelect is THE picker.
   */
  | 'worker'
  /**
   * A PROJECT. 2026-10-19, item #542 part 2 (TCSD-0490): Change project on an
   * invoice line. Scope by `facility_id`. Rows come from fetchProjectRows — the
   * same fetch the invoice page's Project column reads — so the picker and the
   * column cannot name a project two ways.
   */
  | 'project';

export interface SmartSelectScope {
  /** Two-letter state code OR full state name. Normalized to upper 2-letter. */
  state?: string;
  /** Market region exact match (case-insensitive). */
  market_region?: string;
  /** Account scope — facility entity only. */
  account_id?: string;
  /** Category exact match (vendor primary_category or supply category). */
  category?: string;
  /**
   * For entity='service_provider', scope to providers currently active at
   * this facility's state (via service_assignments → facilities). For
   * entity='facility' this filter is a no-op.
   */
  facility_id?: string;
}

export interface SmartSelectRow {
  id: string;
  name: string;
  /** Optional second line shown under the name. */
  subtitle?: string | null;
  /** Optional caption shown when a row matched via an alias. */
  alias_hit?: string | null;
  /** The raw row from the source table, passed to onChange. */
  raw: Record<string, unknown>;
}

interface SmartSelectProps {
  entity: SmartSelectEntity;
  scope?: SmartSelectScope;
  value: string | null;
  onChange: (id: string, row: SmartSelectRow | null) => void;
  /** If provided, a "+ Add new {entity}" row appears below the matches. */
  onCreateNew?: (typedInput: string) => void | Promise<unknown>;
  placeholder?: string;
  /**
   * Optional override label for the entity in UI strings ("+ Add new sub")
   * — defaults to a sensible per-entity label.
   */
  entityLabel?: string;
  disabled?: boolean;
  /**
   * Which rows a person may CHOOSE. 2026-09-02.
   *
   * ⛔ PICKERS EXCLUDE, RESOLVERS RESOLVE — AND A CALL SITE THAT IS BOTH IS
   * SPLIT, NOT NARROWED (TCSD-0163). Filtering the fetch itself would blank the
   * name on every saved record pointing at something now excluded: 81 project
   * service lines point at a Merged or Inactive sub and render "Not assigned
   * yet" in the flat <select> they are replacing, for exactly that reason.
   *
   * So this hides rows from the LIST only. The pool behind `value` is left
   * whole, so an existing choice always still shows its name — it simply cannot
   * be picked again.
   *
   * Omitted, every fetched row is selectable, which is what every call site did
   * before this prop existed.
   */
  isSelectable?: (row: SmartSelectRow) => boolean;
  /**
   * Labelled groups rendered ABOVE this entity's own list, filtered by the same
   * typing and pickable the same way. 2026-09-22.
   *
   * ⛔ THIS IS NOT A SECOND PICKER. It is how one call site says "a search here
   * should also reach a PERSON, and should put the subs who actually worked
   * this week at the top" without any other call site changing: omitted, this
   * component behaves exactly as it did before the prop existed, which is what
   * every existing call site does.
   *
   * A group's rows carry whatever `id` the call site wants selecting to mean —
   * a person row can carry their sub's id, because picking a person on a
   * pay-sheet screen means "that person's sub". `isSelectable` applies to these
   * rows too, so a person whose employer is not recorded yet is listed and
   * says why rather than being silently missing.
   */
  extraGroups?: Array<{ key: string; label: string; rows: SmartSelectRow[] }>;
}

// ---------------------------------------------------------------------------
// State normalization (CO / colorado → CO)
// ---------------------------------------------------------------------------

const STATE_NAME_TO_CODE: Record<string, string> = {
  colorado: 'CO',
  kansas: 'KS',
  texas: 'TX',
  missouri: 'MO',
  oklahoma: 'OK',
  arkansas: 'AR',
  nebraska: 'NE',
  iowa: 'IA',
};

function normalizeState(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (trimmed.length === 2) return trimmed.toUpperCase();
  const code = STATE_NAME_TO_CODE[trimmed.toLowerCase()];
  return code ?? trimmed.toUpperCase();
}

// ---------------------------------------------------------------------------
// Entity config — table + display key + subtitle builder
// ---------------------------------------------------------------------------

interface EntityConfig {
  /** Display label for "+ Add new X" and the placeholder. */
  label: string;
  /** Plural form — "facilities" not "facilitys", "supplies" not "supplys". */
  labelPlural: string;
  /** Default placeholder for the search input. */
  placeholder: string;
  /** Entity_aliases.entity_type value for alias matching. */
  aliasEntityType:
    | 'facility'
    | 'service_provider'
    | 'account'
    | 'employee'
    | 'supply'
    | null;
  /**
   * Rows the call site's `isSelectable` refuses are still LISTED, greyed, with
   * their subtitle saying why — rather than vanishing. 2026-10-19, projects
   * only (the approved #542 demo). The same rule the extra groups already
   * follow: hiding a row a person knows is real makes the search look broken.
   * Every other entity keeps hiding them, exactly as before.
   */
  listUnpickable?: boolean;
}

const ENTITY_CONFIG: Record<SmartSelectEntity, EntityConfig> = {
  service_provider: {
    label: 'sub',
    labelPlural: 'subs',
    placeholder: 'Search subs…',
    aliasEntityType: 'service_provider',
  },
  facility: {
    label: 'facility',
    labelPlural: 'facilities',
    placeholder: 'Search facilities…',
    aliasEntityType: 'facility',
  },
  vendor: {
    label: 'vendor',
    labelPlural: 'vendors',
    placeholder: 'Search vendors…',
    aliasEntityType: null,
  },
  supply: {
    label: 'supply',
    labelPlural: 'supplies',
    placeholder: 'Search supplies…',
    // 2026-09-09: supply aliases exist now — the invoice text of every repaired
    // catalogue row points at the item that survives, so typing what is on the
    // invoice finds the product.
    aliasEntityType: 'supply',
  },
  // 2026-06-09 — person-master search for the contacts junction (Model B).
  // Backed by public.contacts active masters (merged_into_contact_id IS NULL).
  contact: {
    label: 'person',
    labelPlural: 'people',
    placeholder: 'Search people…',
    aliasEntityType: null,
  },
  worker: {
    label: 'worker',
    labelPlural: 'workers',
    placeholder: 'Type a name…',
    aliasEntityType: null,
  },
  project: {
    label: 'project',
    labelPlural: 'projects at other buildings',
    placeholder: 'Search projects…',
    aliasEntityType: null,
    listUnpickable: true,
  },
};

// ---------------------------------------------------------------------------
// Scoped fetchers — one per entity. Each returns SmartSelectRow[].
// `unscoped: true` skips the scope filters (used by the "Show all" path).
// ---------------------------------------------------------------------------

interface FetchOptions {
  unscoped?: boolean;
}

async function fetchFacilities(
  scope: SmartSelectScope,
  opts: FetchOptions = {},
): Promise<SmartSelectRow[]> {
  let q = supabase
    .from('active_facilities')
    .select('facility_id, facility_name, account_id, market_region, state')
    .order('facility_name');
  if (!opts.unscoped) {
    const stateCode = normalizeState(scope.state);
    if (stateCode) q = q.eq('state', stateCode);
    if (scope.market_region)
      q = q.ilike('market_region', scope.market_region);
    if (scope.account_id) q = q.eq('account_id', scope.account_id);
  }
  const { data, error } = await q;
  if (error) throw error;
  type Row = {
    facility_id: string;
    facility_name: string | null;
    account_id: string | null;
    market_region: string | null;
    state: string | null;
  };
  return ((data ?? []) as Row[]).map((r) => ({
    id: r.facility_id,
    name: r.facility_name ?? r.facility_id,
    subtitle:
      [r.market_region, r.state].filter(Boolean).join(' · ') || null,
    raw: r as unknown as Record<string, unknown>,
  }));
}

async function fetchServiceProviders(
  scope: SmartSelectScope,
  opts: FetchOptions = {},
): Promise<SmartSelectRow[]> {
  // Provider scoping is indirect: providers active at facilities in the
  // given state / market. We pull all providers, then if scoped, narrow
  // to provider_ids attached to scoped facilities via service_assignments.
  let allowedIds: Set<string> | null = null;
  if (!opts.unscoped) {
    const stateCode = normalizeState(scope.state);
    if (stateCode || scope.market_region || scope.facility_id) {
      let facQ = supabase.from('facilities').select('facility_id');
      if (stateCode) facQ = facQ.eq('state', stateCode);
      if (scope.market_region)
        facQ = facQ.ilike('market_region', scope.market_region);
      if (scope.facility_id) facQ = facQ.eq('facility_id', scope.facility_id);
      const { data: facData, error: facErr } = await facQ;
      if (facErr) throw facErr;
      const facilityIds = (facData ?? []).map(
        (r) => (r as { facility_id: string }).facility_id,
      );
      if (facilityIds.length === 0) {
        allowedIds = new Set();
      } else {
        const { data: saData, error: saErr } = await supabase
          .from('service_assignments')
          .select('provider_id')
          .in('facility_id', facilityIds)
          .eq('assignment_status', 'Active');
        if (saErr) throw saErr;
        allowedIds = new Set(
          (saData ?? [])
            .map((r) => (r as { provider_id: string | null }).provider_id)
            .filter((v): v is string => Boolean(v)),
        );
      }
    }
  }
  const { data, error } = await supabase
    // provider_class split (2026-06-16): keep non-field vendors (back-office,
    // staffing, professional-services, supply/AP) out of every sub-picker.
    // Status-agnostic on purpose — Pending/Onboarding field subs stay selectable.
    .from('service_providers')
    .select('provider_id, partner_name, coverage_markets, provider_status')
    .eq('provider_class', 'Field_Service')
    .order('partner_name');
  if (error) throw error;
  type Row = {
    provider_id: string;
    partner_name: string | null;
    coverage_markets: string[] | null;
    provider_status: string | null;
  };
  let rows = (data ?? []) as Row[];
  if (allowedIds) rows = rows.filter((r) => allowedIds!.has(r.provider_id));

  // WHO MAY BE PUT ON A BUILDING TODAY — every Active field sub, plus one who
  // is mid-onboarding with a live onboarding record. A sub whose onboarding was
  // cancelled is a dead row and is NOT offered.
  //
  // ⛔ THIS IS A SECOND QUERY, NOT A NARROWER FIRST ONE. Filtering the fetch is
  // what blanked the name on 81 saved lines pointing at a sub who had since
  // been merged or made inactive: a picker has to be able to RENDER what a
  // record already holds, whatever it would offer today. The narrowing happens
  // at the call site through isSelectable.
  //
  // ⛔ AND THE PAPERWORK IS SHOWN, NOT ENFORCED. An account manager who can see
  // that the W-9 has not come back can still pick her, because she is the person
  // cleaning the building on Friday whatever the file says.
  const { data: assignableData, error: assignableErr } = await supabase
    .from('v_sub_assignable')
    .select('provider_id, is_onboarding, onboarding_status, w9_state, coi_state');
  if (assignableErr) throw assignableErr;
  type Assignable = {
    provider_id: string;
    is_onboarding: boolean | null;
    onboarding_status: string | null;
    w9_state: string | null;
    coi_state: string | null;
  };
  const assignable = new Map<string, Assignable>(
    ((assignableData ?? []) as Assignable[]).map((a) => [a.provider_id, a]),
  );

  return rows.map((r) => {
    const a = assignable.get(r.provider_id);
    const paperwork = a?.is_onboarding
      ? [a.onboarding_status, a.w9_state, a.coi_state].filter(Boolean).join(' · ')
      : null;
    return {
      id: r.provider_id,
      name: r.partner_name ?? r.provider_id,
      subtitle:
        [(r.coverage_markets ?? []).map((m) => m.replace(/\s*Metro$/, '')).join(' · '), paperwork ? `onboarding — ${paperwork}` : subStatusLabel(r.provider_status)]
          .filter(Boolean)
          .join(' · ') || null,
      raw: { ...r, assignable: Boolean(a) } as unknown as Record<string, unknown>,
    };
  });
}

/**
 * The narrowing for every sub picker. Pass as isSelectable so a saved line
 * pointing at a sub we would no longer offer still RENDERS the name.
 */
export function isAssignableSub(row: SmartSelectRow): boolean {
  return row.raw?.assignable === true;
}

async function fetchVendors(
  scope: SmartSelectScope,
  opts: FetchOptions = {},
): Promise<SmartSelectRow[]> {
  let q = supabase
    .from('preferred_vendors')
    .select(
      'vendor_id, vendor_name, primary_category, coverage_type, same_day_capable, status',
    )
    .order('vendor_name');
  if (!opts.unscoped && scope.category) {
    q = q.ilike('primary_category', scope.category);
  }
  const { data, error } = await q;
  if (error) throw error;
  type Row = {
    vendor_id: string;
    vendor_name: string | null;
    primary_category: string | null;
    coverage_type: string | null;
    same_day_capable: string | null;
    status: string | null;
  };
  return ((data ?? []) as Row[]).map((r) => ({
    id: r.vendor_id,
    name: r.vendor_name ?? r.vendor_id,
    subtitle:
      [
        r.primary_category,
        r.coverage_type,
        r.same_day_capable === 'Yes' ? 'same-day' : null,
      ]
        .filter(Boolean)
        .join(' · ') || null,
    raw: r as unknown as Record<string, unknown>,
  }));
}

async function fetchSupplies(
  scope: SmartSelectScope,
  opts: FetchOptions = {},
): Promise<SmartSelectRow[]> {
  let q = supabase
    .from('supplies')
    // is_active and merged_into_supply_id ride along so a call site can hide a
    // retired catalogue row with isSelectable. ⛔ They are NOT filtered here:
    // filtering the fetch blanks the name on every saved record pointing at
    // something now excluded (TCSD-0163).
    .select(
      'supply_id, item_name, supply_category, sub_category, unit_cost, is_active, merged_into_supply_id',
    )
    .order('item_name');
  if (!opts.unscoped && scope.category) {
    q = q.ilike('supply_category', scope.category);
  }
  const { data, error } = await q;
  if (error) throw error;
  type Row = {
    supply_id: string;
    item_name: string | null;
    supply_category: string | null;
    sub_category: string | null;
    unit_cost: number | null;
    is_active: boolean | null;
    merged_into_supply_id: string | null;
  };
  return ((data ?? []) as Row[]).map((r) => ({
    id: r.supply_id,
    name: r.item_name ?? r.supply_id,
    subtitle:
      [
        r.supply_category,
        r.sub_category,
        r.unit_cost != null ? `$${r.unit_cost.toFixed(2)}` : null,
      ]
        .filter(Boolean)
        .join(' · ') || null,
    raw: r as unknown as Record<string, unknown>,
  }));
}

async function fetchContacts(
  _scope: SmartSelectScope,
  _opts: FetchOptions = {},
): Promise<SmartSelectRow[]> {
  // Person-master pool: active contacts only (merged duplicates are hidden).
  // No scope filter — a person can be linked to any facility/account.
  const { data, error } = await supabase
    .from('contacts')
    .select('contact_id, first_name, last_name, preferred_name, email, job_title')
    .is('merged_into_contact_id', null)
    .order('last_name', { ascending: true })
    .order('first_name', { ascending: true });
  if (error) throw error;
  type Row = {
    contact_id: string;
    first_name: string | null;
    last_name: string | null;
    preferred_name: string | null;
    email: string | null;
    job_title: string | null;
  };
  return ((data ?? []) as Row[]).map((r) => {
    const name =
      (r.preferred_name
        ? `${r.preferred_name} ${r.last_name ?? ''}`
        : `${r.first_name ?? ''} ${r.last_name ?? ''}`
      ).trim() || r.email || r.contact_id;
    return {
      id: r.contact_id,
      name,
      subtitle: [r.job_title, r.email].filter(Boolean).join(' · ') || null,
      raw: r as unknown as Record<string, unknown>,
    };
  });
}

/**
 * Punch-clock workers, from the picker view that already decides who is
 * offerable (active only — TCSD-0129).
 *
 * ⛔ The subtitle is what makes this findable rather than merely searchable.
 * FIVE active workers are called Rodriguez, so a name alone does not identify
 * anybody: every row says whether they are set up to punch, and a night
 * manager says so. `market_region` scopes it when a caller has one.
 */
async function fetchWorkers(
  scope: SmartSelectScope,
  opts: FetchOptions = {},
): Promise<SmartSelectRow[]> {
  const { data, error } = await supabase
    .from('v_time_worker_picker')
    .select('worker_id, display_name, employment_class, has_pin, active_assignments, night_manager_market')
    .order('display_name', { ascending: true });
  if (error) throw error;
  type Row = {
    worker_id: string;
    display_name: string | null;
    employment_class: string | null;
    has_pin: boolean | null;
    active_assignments: number | null;
    night_manager_market: string | null;
  };
  const rows = ((data ?? []) as Row[]).filter((r) => {
    if (opts.unscoped || !scope.market_region) return true;
    return (r.night_manager_market ?? '').toLowerCase() === scope.market_region.toLowerCase();
  });
  return rows.map((r) => ({
    id: r.worker_id,
    name: r.display_name ?? r.worker_id,
    subtitle: [
      r.night_manager_market ? `Night manager · ${r.night_manager_market}` : null,
      r.has_pin ? 'Set up to punch' : 'No PIN yet',
    ].filter(Boolean).join(' · ') || null,
    raw: r as unknown as Record<string, unknown>,
  }));
}

async function fetchScopedRows(
  entity: SmartSelectEntity,
  scope: SmartSelectScope,
  opts: FetchOptions = {},
): Promise<SmartSelectRow[]> {
  switch (entity) {
    case 'facility':
      return fetchFacilities(scope, opts);
    case 'service_provider':
      return fetchServiceProviders(scope, opts);
    case 'vendor':
      return fetchVendors(scope, opts);
    case 'supply':
      return fetchSupplies(scope, opts);
    case 'contact':
      return fetchContacts(scope, opts);
    case 'worker':
      return fetchWorkers(scope, opts);
    case 'project':
      return fetchProjects(scope, opts);
  }
}

/**
 * Projects, for Change project on an invoice line (item #542 part 2). Scoped by
 * facility_id; "Show all" reads every project and names each one's building.
 * The subtitle carries the facts that decide whether it can be picked — stage
 * and whether it is already on an invoice — so a greyed row says why.
 */
function projectToRow(r: ProjectRow, withBuilding: boolean): SmartSelectRow {
  const money =
    r.bill_amount != null
      ? `$${Number(r.bill_amount).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
      : null;
  return {
    id: r.project_id,
    name: projectRowName(r),
    subtitle:
      [
        withBuilding ? r.facility_name : null,
        money,
        projectStageLabel(r.stage),
        r.invoice_id ? `on invoice ${r.invoice_id}` : 'not on any invoice',
      ]
        .filter(Boolean)
        .join(' · ') || null,
    raw: r as unknown as Record<string, unknown>,
  };
}

async function fetchProjects(
  scope: SmartSelectScope,
  opts: FetchOptions = {},
): Promise<SmartSelectRow[]> {
  const scoped = !opts.unscoped && Boolean(scope.facility_id);
  const rows = await fetchProjectRows(scoped ? { facilityId: scope.facility_id } : {});
  return rows.map((r) => projectToRow(r, !scoped));
}

// ---------------------------------------------------------------------------
// One saved value, by id — the last step in resolving what the box reads.
// 2026-10-01, Build Room item 654.
//
// ⛔ A SAVED VALUE NAMES SOMEBODY WHATEVER LIST IS SHOWING (TCSD-0163). The
// box used to look the value up only in the list on screen, so a project line
// whose sub does not clean that building at night read BLANK, and "Show all"
// made the name appear until the next reload. This lookup reads the record
// itself, unscoped, and ignores every narrowing a list applies —
// provider_class, merged, inactive, `isSelectable` — because what may newly be
// CHOSEN is a separate question from whose name is already on the record.
//
// One row, not the whole unscoped list. Cached per entity and id for the life
// of the page, so a project with several lines asks once.
// ---------------------------------------------------------------------------

const byIdCache = new Map<string, Promise<SmartSelectRow | null>>();

async function fetchRowByIdUncached(
  entity: SmartSelectEntity,
  id: string,
): Promise<SmartSelectRow | null> {
  switch (entity) {
    case 'facility': {
      const { data, error } = await supabase
        .from('facilities')
        .select('facility_id, facility_name, account_id, market_region, state')
        .eq('facility_id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as {
        facility_id: string;
        facility_name: string | null;
        market_region: string | null;
        state: string | null;
      };
      return {
        id: r.facility_id,
        name: r.facility_name ?? r.facility_id,
        subtitle: [r.market_region, r.state].filter(Boolean).join(' · ') || null,
        raw: r as unknown as Record<string, unknown>,
      };
    }
    case 'service_provider': {
      const { data, error } = await supabase
        .from('service_providers')
        .select('provider_id, partner_name, coverage_markets, provider_status')
        .eq('provider_id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as {
        provider_id: string;
        partner_name: string | null;
        coverage_markets: string[] | null;
        provider_status: string | null;
      };
      return {
        id: r.provider_id,
        name: r.partner_name ?? r.provider_id,
        subtitle: [(r.coverage_markets ?? []).join(' · ') || null, r.provider_status].filter(Boolean).join(' · ') || null,
        raw: r as unknown as Record<string, unknown>,
      };
    }
    case 'vendor': {
      const { data, error } = await supabase
        .from('preferred_vendors')
        .select('vendor_id, vendor_name, primary_category')
        .eq('vendor_id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as { vendor_id: string; vendor_name: string | null; primary_category: string | null };
      return {
        id: r.vendor_id,
        name: r.vendor_name ?? r.vendor_id,
        subtitle: r.primary_category,
        raw: r as unknown as Record<string, unknown>,
      };
    }
    case 'supply': {
      const { data, error } = await supabase
        .from('supplies')
        .select('supply_id, item_name, supply_category, sub_category, is_active, merged_into_supply_id')
        .eq('supply_id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as {
        supply_id: string;
        item_name: string | null;
        supply_category: string | null;
        sub_category: string | null;
      };
      return {
        id: r.supply_id,
        name: r.item_name ?? r.supply_id,
        subtitle: [r.supply_category, r.sub_category].filter(Boolean).join(' · ') || null,
        raw: r as unknown as Record<string, unknown>,
      };
    }
    case 'contact': {
      // Merged duplicates included on purpose: a record that points at one
      // still names the person.
      const { data, error } = await supabase
        .from('contacts')
        .select('contact_id, first_name, last_name, preferred_name, email, job_title')
        .eq('contact_id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as {
        contact_id: string;
        first_name: string | null;
        last_name: string | null;
        preferred_name: string | null;
        email: string | null;
        job_title: string | null;
      };
      const name =
        (r.preferred_name
          ? `${r.preferred_name} ${r.last_name ?? ''}`
          : `${r.first_name ?? ''} ${r.last_name ?? ''}`
        ).trim() || r.email || r.contact_id;
      return {
        id: r.contact_id,
        name,
        subtitle: [r.job_title, r.email].filter(Boolean).join(' · ') || null,
        raw: r as unknown as Record<string, unknown>,
      };
    }
    case 'worker': {
      // The picker view lists active workers only (TCSD-0129); a saved value
      // may name one who has since left, so this reads the table.
      const { data, error } = await supabase
        .from('workers')
        .select('worker_id, preferred_name, first_name, last_name')
        .eq('worker_id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as {
        worker_id: string;
        preferred_name: string | null;
        first_name: string | null;
        last_name: string | null;
      };
      const name = `${r.preferred_name ?? r.first_name ?? ''} ${r.last_name ?? ''}`.trim();
      return {
        id: r.worker_id,
        name: name || r.worker_id,
        subtitle: null,
        raw: r as unknown as Record<string, unknown>,
      };
    }
    case 'project': {
      const [r] = await fetchProjectRows({ projectIds: [id] });
      return r ? projectToRow(r, true) : null;
    }
  }
}

function fetchRowById(entity: SmartSelectEntity, id: string): Promise<SmartSelectRow | null> {
  const key = `${entity}:${id}`;
  const hit = byIdCache.get(key);
  if (hit) return hit;
  const p = fetchRowByIdUncached(entity, id).catch((err) => {
    // A failed read is not "nobody" — forget it so the next render asks again.
    byIdCache.delete(key);
    throw err;
  });
  byIdCache.set(key, p);
  return p;
}

// ---------------------------------------------------------------------------
// Alias matches — entity_aliases (entity_type, entity_id, alias)
// ---------------------------------------------------------------------------

async function fetchAliasMatches(
  entity: SmartSelectEntity,
  query: string,
): Promise<{ entity_id: string; alias: string }[]> {
  const cfg = ENTITY_CONFIG[entity];
  if (!cfg.aliasEntityType || query.trim().length === 0) return [];
  const { data, error } = await supabase
    .from('entity_aliases')
    .select('entity_id, alias')
    .eq('entity_type', cfg.aliasEntityType)
    .ilike('alias', `%${query.trim()}%`)
    .limit(50);
  if (error) {
    // Aliases are a UX nicety — never block the dropdown on alias failures.
    console.warn('[smart-select] alias query failed', error);
    return [];
  }
  type Row = { entity_id: string; alias: string };
  return (data ?? []) as Row[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const VISIBLE_LIMIT = 8;

export function SmartSelect({
  entity,
  scope,
  value,
  onChange,
  onCreateNew,
  placeholder,
  entityLabel,
  disabled,
  isSelectable,
  extraGroups,
}: SmartSelectProps) {
  const cfg = ENTITY_CONFIG[entity];
  const label = entityLabel ?? cfg.label;
  const labelPlural = entityLabel ? `${entityLabel}s` : cfg.labelPlural;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [scoped, setScoped] = useState<SmartSelectRow[]>([]);
  const [scopedLoading, setScopedLoading] = useState(false);
  const [showingAll, setShowingAll] = useState(false);
  const [allRows, setAllRows] = useState<SmartSelectRow[] | null>(null);
  const [allLoading, setAllLoading] = useState(false);
  const [aliasHits, setAliasHits] = useState<Map<string, string>>(new Map());

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Stable scope key for the effect dep.
  const scopeKey = useMemo(
    () => JSON.stringify(scope ?? {}),
    [scope],
  );

  // Load scoped rows whenever entity or scope changes.
  useEffect(() => {
    let cancelled = false;
    setScopedLoading(true);
    fetchScopedRows(entity, scope ?? {})
      .then((rows) => {
        if (cancelled) return;
        setScoped(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[smart-select] scope load failed', err);
        setScoped([]);
      })
      .finally(() => {
        if (!cancelled) setScopedLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, scopeKey]);

  // Load "show all" lazily when the user expands.
  useEffect(() => {
    if (!showingAll || allRows !== null) return;
    let cancelled = false;
    setAllLoading(true);
    fetchScopedRows(entity, {}, { unscoped: true })
      .then((rows) => {
        if (cancelled) return;
        setAllRows(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[smart-select] all-rows load failed', err);
        setAllRows([]);
      })
      .finally(() => {
        if (!cancelled) setAllLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showingAll, allRows, entity]);

  // Reset "show all" when entity changes — different unfiltered set.
  useEffect(() => {
    setAllRows(null);
    setShowingAll(false);
  }, [entity]);

  // Alias lookup as the user types.
  useEffect(() => {
    let cancelled = false;
    if (query.trim().length < 2) {
      setAliasHits(new Map());
      return;
    }
    fetchAliasMatches(entity, query).then((rows) => {
      if (cancelled) return;
      const m = new Map<string, string>();
      for (const r of rows) if (!m.has(r.entity_id)) m.set(r.entity_id, r.alias);
      setAliasHits(m);
    });
    return () => {
      cancelled = true;
    };
  }, [query, entity]);

  // Click-outside closes.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Keep active option in view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLLIElement>(
      `li[data-idx="${activeIdx}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  // Reset active index when filter / list shape changes.
  useEffect(() => {
    setActiveIdx(0);
  }, [query, open, showingAll]);

  /**
   * What the closed box reads. ⛔ A SAVED VALUE ALWAYS RESOLVES TO A NAME,
   * WHATEVER LIST IS SHOWING (TCSD-0163). In order: this entity's scoped rows,
   * the "Show all" rows if they were ever loaded, the call site's own groups,
   * and finally ONE read of the record by id, unscoped and un-narrowed
   * (`fetchRowById`). Before 2026-10-01 only the list on screen was searched,
   * so a value outside the scope read blank until "Show all" was opened.
   *
   * Groups come after the entity's own rows on purpose: a group row may carry
   * an id that is not its own name — the pay-sheet picker lists a PERSON under
   * their sub's id — and the box must still name the sub.
   */
  const listed = useMemo<SmartSelectRow | null>(() => {
    if (!value) return null;
    return (
      scoped.find((r) => r.id === value) ??
      allRows?.find((r) => r.id === value) ??
      (extraGroups ?? []).flatMap((g) => g.rows).find((r) => r.id === value) ??
      null
    );
  }, [scoped, allRows, extraGroups, value]);

  const lookupKey = value ? `${entity}:${value}` : null;
  const [lookup, setLookup] = useState<{ key: string; row: SmartSelectRow | null } | null>(null);
  const [lookupFailed, setLookupFailed] = useState<string | null>(null);

  useEffect(() => {
    if (!value || !lookupKey || listed || scopedLoading) return;
    if (lookup?.key === lookupKey) return;
    let cancelled = false;
    fetchRowById(entity, value)
      .then((row) => {
        if (!cancelled) setLookup({ key: lookupKey, row });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[smart-select] saved-value lookup failed', err);
        setLookupFailed(lookupKey);
      });
    return () => {
      cancelled = true;
    };
  }, [entity, value, lookupKey, listed, scopedLoading, lookup]);

  const selected: SmartSelectRow | null =
    listed ?? (lookup && lookup.key === lookupKey ? lookup.row : null);

  /**
   * A value is saved and its name is still on the way. The box shows the same
   * "Loading…" the list does — never blank, never the placeholder, which would
   * read as "nobody chosen". A value that matches no record at all, or a read
   * that failed, falls through to the placeholder exactly as before.
   */
  const resolving =
    Boolean(value) &&
    !selected &&
    lookupFailed !== lookupKey &&
    (scopedLoading || !lookup || lookup.key !== lookupKey);

  // ⛔ The SELECTABLE set. `scoped` itself stays whole above — `listed` reads
  // it, and `fetchRowById` backs it, so a saved value always resolves to a name
  // even when it can no longer be chosen and even when it is outside the scope
  // (TCSD-0163). Only the list is narrowed.
  const selectable = useMemo<SmartSelectRow[]>(
    () => (isSelectable ? scoped.filter(isSelectable) : scoped),
    [scoped, isSelectable],
  );

  /**
   * The call site's own groups, filtered by the same typing. ⛔ Rows already
   * shown here are removed from the entity's own list below, so one sub never
   * appears twice in the same dropdown.
   */
  const shownGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (extraGroups ?? [])
      .map((g) => ({
        ...g,
        rows: g.rows.filter((r) => q === '' || r.name.toLowerCase().includes(q)
          || (r.subtitle ?? '').toLowerCase().includes(q)),
      }))
      .filter((g) => g.rows.length > 0);
  }, [extraGroups, query]);

  const groupedIds = useMemo(
    () => new Set(shownGroups.flatMap((g) => g.rows.map((r) => r.id))),
    [shownGroups],
  );

  // Filter + alias-fold the selectable set.
  const filteredScoped = useMemo<SmartSelectRow[]>(() => {
    const q = query.trim().toLowerCase();
    const aliasHitForRow = (id: string): string | null =>
      aliasHits.get(id) ?? null;
    if (!q) {
      return selectable.map((r) => ({ ...r, alias_hit: aliasHitForRow(r.id) }));
    }
    const byName = selectable.filter((r) => r.name.toLowerCase().includes(q));
    const byNameIds = new Set(byName.map((r) => r.id));
    const byAlias = selectable
      .filter((r) => !byNameIds.has(r.id) && aliasHits.has(r.id))
      .map((r) => ({ ...r, alias_hit: aliasHits.get(r.id) ?? null }));
    return [
      ...byName.map((r) => ({ ...r, alias_hit: aliasHitForRow(r.id) })),
      ...byAlias,
    ];
  }, [selectable, query, aliasHits]);

  // Filter the "Show all" set (when expanded) — same name+alias logic but
  // against the unfiltered pool, minus rows already shown above.
  const filteredAll = useMemo<SmartSelectRow[]>(() => {
    if (!showingAll || !allRows) return [];
    const q = query.trim().toLowerCase();
    const alreadyShown = new Set(filteredScoped.map((r) => r.id));
    // "Show all" widens the SCOPE, never the rule about what may be chosen.
    const pool = isSelectable ? allRows.filter(isSelectable) : allRows;
    const base = q
      ? pool.filter((r) => r.name.toLowerCase().includes(q))
      : pool;
    return base.filter((r) => !alreadyShown.has(r.id));
  }, [showingAll, allRows, query, filteredScoped, isSelectable]);

  // Visible scoped — capped at VISIBLE_LIMIT unless the user has expanded, and
  // never repeating a row an extra group already showed above.
  const visibleScoped = useMemo(() => {
    const rest = filteredScoped.filter((r) => !groupedIds.has(r.id));
    return showingAll ? rest : rest.slice(0, VISIBLE_LIMIT);
  }, [filteredScoped, showingAll, groupedIds]);

  /** The extra groups' rows, in render order, for keyboard navigation. */
  const groupRows = useMemo(
    () => shownGroups.flatMap((g) => (isSelectable ? g.rows.filter(isSelectable) : g.rows)),
    [shownGroups, isSelectable],
  );

  const hasMoreScoped =
    !showingAll && filteredScoped.length > VISIBLE_LIMIT;

  // Rows the call site refuses, LISTED greyed with their reason — only for an
  // entity whose config asks for it (projects, 2026-10-19). Never pickable,
  // never in keyboard navigation.
  const matchesQuery = useCallback(
    (r: SmartSelectRow) => {
      const q = query.trim().toLowerCase();
      return q === '' || r.name.toLowerCase().includes(q) || (r.subtitle ?? '').toLowerCase().includes(q);
    },
    [query],
  );
  const unpickableScoped = useMemo<SmartSelectRow[]>(
    () =>
      cfg.listUnpickable && isSelectable
        ? scoped.filter((r) => !isSelectable(r) && matchesQuery(r))
        : [],
    [cfg.listUnpickable, isSelectable, scoped, matchesQuery],
  );
  const unpickableAll = useMemo<SmartSelectRow[]>(() => {
    if (!cfg.listUnpickable || !isSelectable || !showingAll || !allRows) return [];
    const inScope = new Set(scoped.map((r) => r.id));
    return allRows.filter((r) => !inScope.has(r.id) && !isSelectable(r) && matchesQuery(r));
  }, [cfg.listUnpickable, isSelectable, showingAll, allRows, scoped, matchesQuery]);

  // Build flat list of selectable options (matches first, then unfiltered
  // expansion). Used for keyboard navigation.
  const flatOptions = useMemo<SmartSelectRow[]>(
    () => [...groupRows, ...visibleScoped, ...filteredAll],
    [groupRows, visibleScoped, filteredAll],
  );

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [disabled]);

  function pick(row: SmartSelectRow) {
    onChange(row.id, row);
    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) =>
        Math.min(i + 1, Math.max(0, flatOptions.length - 1)),
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = flatOptions[activeIdx];
      if (opt) pick(opt);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  const buttonPlaceholder = placeholder ?? cfg.placeholder;

  // Render -----------------------------------------------------------------
  return (
    <div ref={wrapperRef} className="relative">
      {!open ? (
        <button
          type="button"
          onClick={handleOpen}
          disabled={disabled}
          className="flex w-full items-center justify-between rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-left text-sm text-tcs-text hover:border-tcs-border-input focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className={selected ? '' : 'text-tcs-text-muted'}>
            {selected ? selected.name : resolving ? 'Loading…' : buttonPlaceholder}
          </span>
          <ChevronDown className="h-4 w-4 text-tcs-text-muted" />
        </button>
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={buttonPlaceholder}
          className="w-full rounded-md border border-tcs-text-muted bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text-muted"
        />
      )}

      {open && (
        <ul
          ref={listRef}
          className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-tcs-border bg-tcs-overlay text-sm shadow-lg"
          role="listbox"
        >
          {/* The call site's own groups, first ------------------------- */}
          {shownGroups.map((g) => {
            const rows = isSelectable ? g.rows.filter(isSelectable) : g.rows;
            const unpickable = g.rows.filter((r) => !rows.includes(r));
            return (
              <div key={g.key}>
                <li className="border-t border-tcs-border bg-tcs-bg-muted px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                  {g.label}
                </li>
                {rows.map((opt) =>
                  renderOption(opt, groupRows.indexOf(opt), activeIdx, pick, setActiveIdx),
                )}
                {/* ⛔ A row that cannot be chosen is SHOWN and says why. Hiding
                    it makes the search look broken to the person who typed a
                    name they know is real. */}
                {unpickable.map((opt) => (
                  <li key={`${g.key}-${opt.id}-${opt.name}`} className="px-2 py-1.5 text-tcs-text-muted">
                    <div className="font-medium leading-tight">{opt.name}</div>
                    {opt.subtitle && <div className="mt-0.5 text-[11px]">{opt.subtitle}</div>}
                  </li>
                ))}
              </div>
            );
          })}

          {/* Scoped header -------------------------------------------- */}
          {scopedLoading ? (
            <li className="px-2 py-2 text-tcs-text-muted">Loading…</li>
          ) : visibleScoped.length === 0 && !hasMoreScoped && shownGroups.length === 0 && unpickableScoped.length === 0 ? (
            <li className="px-2 py-2 text-tcs-text-muted">
              No matches in scope
            </li>
          ) : null}

          {visibleScoped.map((opt, idx) =>
            renderOption(opt, groupRows.length + idx, activeIdx, pick, setActiveIdx),
          )}

          {unpickableScoped.map((opt) => renderUnpickable(opt, 'scoped'))}

          {hasMoreScoped && (
            <li
              key="__more__"
              onMouseDown={(e) => {
                e.preventDefault();
                setShowingAll(true);
              }}
              className="cursor-pointer border-t border-tcs-border px-2 py-1.5 text-xs text-tcs-text-muted hover:bg-tcs-bg-muted"
            >
              Show all matching scope ({filteredScoped.length})
            </li>
          )}

          {/* + Add new ------------------------------------------------ */}
          {onCreateNew && (
            <li
              key="__addnew__"
              onMouseDown={(e) => {
                e.preventDefault();
                void onCreateNew(query.trim());
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-1 border-t border-tcs-border bg-tcs-bg-muted/60 px-2 py-1.5 text-tcs-text-secondary hover:bg-tcs-surface"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>
                Add new {label}
                {query.trim() && (
                  <span className="text-tcs-text-muted"> · &ldquo;{query.trim()}&rdquo;</span>
                )}
              </span>
            </li>
          )}

          {/* Show all toggle ----------------------------------------- */}
          {!showingAll ? (
            <li
              key="__showall__"
              onMouseDown={(e) => {
                e.preventDefault();
                setShowingAll(true);
              }}
              className="cursor-pointer border-t border-tcs-border px-2 py-1.5 text-xs text-tcs-text-muted hover:bg-tcs-bg-muted"
            >
              Show all {labelPlural}
            </li>
          ) : null}

          {/* Unfiltered set when expanded --------------------------- */}
          {showingAll && (
            <>
              <li
                key="__allheader__"
                className="border-t border-tcs-border bg-tcs-bg-muted px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-tcs-text-muted"
              >
                All {labelPlural}
                {allLoading ? ' · loading…' : ''}
              </li>
              {filteredAll.length === 0 && unpickableAll.length === 0 && !allLoading ? (
                <li className="px-2 py-2 text-tcs-text-muted">No more matches</li>
              ) : null}
              {filteredAll.map((opt, idx) =>
                renderOption(
                  opt,
                  groupRows.length + visibleScoped.length + idx,
                  activeIdx,
                  pick,
                  setActiveIdx,
                ),
              )}
              {unpickableAll.map((opt) => renderUnpickable(opt, 'all'))}
            </>
          )}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row renderer
// ---------------------------------------------------------------------------

/** A row that cannot be chosen: shown, greyed, with its subtitle saying why. */
function renderUnpickable(opt: SmartSelectRow, keyPrefix: string): ReactNode {
  return (
    <li
      key={`${keyPrefix}-unpickable-${opt.id}`}
      aria-disabled="true"
      className="cursor-default px-2 py-1.5 text-tcs-text-muted"
    >
      <div className="font-medium leading-tight">{opt.name}</div>
      {opt.subtitle && <div className="mt-0.5 text-[11px]">{opt.subtitle}</div>}
    </li>
  );
}

function renderOption(
  opt: SmartSelectRow,
  idx: number,
  activeIdx: number,
  pick: (row: SmartSelectRow) => void,
  setActiveIdx: (i: number) => void,
): ReactNode {
  const active = idx === activeIdx;
  return (
    <li
      key={opt.id}
      data-idx={idx}
      role="option"
      aria-selected={active}
      onMouseDown={(e) => {
        e.preventDefault();
        pick(opt);
      }}
      onMouseEnter={() => setActiveIdx(idx)}
      className={`cursor-pointer px-2 py-1.5 ${
        active
          ? 'bg-tcs-inverse text-tcs-inverse-ink'
          : 'text-tcs-text hover:bg-tcs-surface'
      }`}
    >
      <div className="font-medium leading-tight">{opt.name}</div>
      {(opt.subtitle || opt.alias_hit) && (
        <div
          className={`mt-0.5 text-[11px] ${
            // TODO(cw-j phase 1c): text-tcs-text-faint is a deliberately faint
            // decorative glyph, not body text. Snapping it to muted makes it
            // notably more prominent — a design change, not a token migration.
            active ? 'text-tcs-text-faint' : 'text-tcs-text-muted'
          }`}
        >
          {opt.subtitle}
          {opt.alias_hit ? (
            <span className="italic"> (alias: &ldquo;{opt.alias_hit}&rdquo;)</span>
          ) : null}
        </div>
      )}
    </li>
  );
}
